import {
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
} from "lib/headlessui/components/menu/menu";
import {
    ChevronDownIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { UserId } from "@sp-crm/core";
import { useQuery } from "@tanstack/react-query";
import { Content, PageHeader, Stage } from "components/layout";
import { trpc } from "components/rpc/trpc";
import { SelectableUser, UserSelect } from "components/shared/user-select";
import { TourEditor } from "components/tours/tour-editor";
import { TourDto } from "components/tours/types";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { useCurrentUserId } from "store/selectors/hooks";
import { calendarSlice } from "store/slices/calendar";
import { CalendarEvent, TaskCalendarDto } from "./calendar-types";
import {
    DAYS_OF_WEEK,
    MONTH_ABBREVIATIONS,
    MONTH_NAMES,
    formatDateKey,
    formatTime,
    getGridDates,
    getWeekDays,
    isSameDay,
    parseDateTimeToDateKey,
} from "./calendar-utils";
import { DayGrid } from "./day-grid";
import { TaskDetailPanel } from "./task-detail-panel";
import { WeekGrid } from "./week-grid";

type ViewMode = "month" | "week" | "day";

interface MonthState {
    year: number;
    month: number;
}

const MonthPicker: React.FC<{
    currentMonth: MonthState;
    onSelect: (year: number, month: number) => void;
}> = ({ currentMonth, onSelect }) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const selectedRef = useRef<HTMLButtonElement>(null);

    const months = useMemo(() => {
        const result: { year: number; month: number }[] = [];
        for (let offset = -12; offset <= 12; offset++) {
            const totalMonths = currentMonth.year * 12 + currentMonth.month + offset;
            result.push({
                year: Math.floor(totalMonths / 12),
                month: totalMonths % 12,
            });
        }
        return result;
    }, [currentMonth.year, currentMonth.month]);

    useEffect(() => {
        selectedRef.current?.scrollIntoView({
            inline: "center",
            block: "nearest",
        });
    }, []);

    return (
        <div className="border border-gray-200 rounded-lg p-3 mb-4 bg-white">
            <div
                ref={scrollRef}
                className="flex items-center gap-2 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                {months.map((m, idx) => {
                    const isSelected =
                        m.year === currentMonth.year && m.month === currentMonth.month;
                    const showYear = idx === 0 || months[idx - 1].year !== m.year;
                    return (
                        <React.Fragment key={`${m.year}-${m.month}`}>
                            {showYear && (
                                <span className="text-xs font-medium text-gray-500 shrink-0 px-1">
                                    {m.year}
                                </span>
                            )}
                            <button
                                ref={isSelected ? selectedRef : undefined}
                                onClick={() => onSelect(m.year, m.month)}
                                className={`shrink-0 px-3 py-1.5 text-sm rounded-full ${
                                    isSelected
                                        ? "bg-brand-600 text-white font-semibold"
                                        : "border border-gray-300 text-gray-700 hover:bg-gray-50"
                                }`}>
                                {MONTH_ABBREVIATIONS[m.month]}
                            </button>
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );
};

export const CalendarPage: React.FC = () => {
    const dispatch = useAppDispatch();
    const currentUserId = useCurrentUserId();
    const viewMode = useAppSelector(state => state.calendar.viewMode);
    const selectedUserId = useAppSelector(state => state.calendar.selectedUserId);

    const handleUserChange = useCallback(
        (userId: SelectableUser) => {
            const val =
                userId && userId !== currentUserId ? (userId as UserId) : undefined;
            dispatch(calendarSlice.actions.setSelectedUserId(val));
        },
        [dispatch, currentUserId],
    );
    const today = useMemo(() => new Date(), []);
    const [currentMonth, setCurrentMonth] = useState<MonthState>({
        year: today.getFullYear(),
        month: today.getMonth(),
    });
    const [currentWeekDate, setCurrentWeekDate] = useState<Date>(today);
    const [currentDayDate, setCurrentDayDate] = useState<Date>(today);
    const [selectedTour, setSelectedTour] = useState<TourDto | null>(null);
    const [selectedTask, setSelectedTask] = useState<TaskCalendarDto | null>(null);
    const [miniCalExpanded, setMiniCalExpanded] = useState(false);
    const [miniCalMonth, setMiniCalMonth] = useState<MonthState>({
        year: today.getFullYear(),
        month: today.getMonth(),
    });

    useEffect(() => {
        if (viewMode === "day") {
            setMiniCalMonth({
                year: currentDayDate.getFullYear(),
                month: currentDayDate.getMonth(),
            });
        } else if (viewMode === "week") {
            setMiniCalMonth({
                year: currentWeekDate.getFullYear(),
                month: currentWeekDate.getMonth(),
            });
        } else {
            setMiniCalMonth(currentMonth);
        }
    }, [viewMode, currentDayDate, currentWeekDate, currentMonth]);

    const gridDates = useMemo(
        () => getGridDates(currentMonth.year, currentMonth.month),
        [currentMonth.year, currentMonth.month],
    );

    const miniCalDates = useMemo(
        () => getGridDates(miniCalMonth.year, miniCalMonth.month),
        [miniCalMonth.year, miniCalMonth.month],
    );

    const weekDays = useMemo(() => getWeekDays(currentWeekDate), [currentWeekDate]);

    const { startDate, endDate } = useMemo(() => {
        if (viewMode === "day") {
            const dayKey = formatDateKey(currentDayDate);
            return { startDate: dayKey, endDate: dayKey };
        }
        if (viewMode === "week") {
            return {
                startDate: formatDateKey(weekDays[0]),
                endDate: formatDateKey(weekDays[6]),
            };
        }
        return {
            startDate: formatDateKey(gridDates[0].date),
            endDate: formatDateKey(gridDates[gridDates.length - 1].date),
        };
    }, [viewMode, weekDays, gridDates, currentDayDate]);

    const trpcClient = trpc.useTRPC();
    const toursQueryOptions = useMemo(
        () =>
            trpcClient.tours.list.queryOptions({
                startDate,
                endDate,
                userId: selectedUserId ?? undefined,
            }),
        [trpcClient, startDate, endDate, selectedUserId],
    );
    const tasksQueryOptions = useMemo(
        () =>
            trpcClient.tasks.listForCalendar.queryOptions({
                startDate,
                endDate,
                userId: selectedUserId ?? undefined,
            }),
        [trpcClient, startDate, endDate, selectedUserId],
    );

    const toursQuery = useQuery(toursQueryOptions);
    const tasksQuery = useQuery(tasksQueryOptions);

    const tours = useMemo(() => toursQuery.data || [], [toursQuery.data]);
    const tasks = useMemo(() => tasksQuery.data || [], [tasksQuery.data]);

    const eventsByDate = useMemo(() => {
        const map = new Map<string, CalendarEvent[]>();

        const addEvent = (dateKey: string, event: CalendarEvent) => {
            if (!map.has(dateKey)) {
                map.set(dateKey, []);
            }
            map.get(dateKey)!.push(event);
        };

        for (const tour of tours) {
            addEvent(tour.tourDate, { kind: "tour", data: tour });
        }

        for (const task of tasks) {
            const dateKey = task.dueDate
                ? task.dueDate
                : task.dueDateTime
                  ? parseDateTimeToDateKey(task.dueDateTime)
                  : null;
            if (dateKey) {
                addEvent(dateKey, { kind: "task", data: task });
            }
        }

        return map;
    }, [tours, tasks]);

    const handlePrev = useCallback(() => {
        if (viewMode === "day") {
            setCurrentDayDate(prev => {
                const d = new Date(prev);
                d.setDate(d.getDate() - 1);
                return d;
            });
        } else if (viewMode === "week") {
            setCurrentWeekDate(prev => {
                const d = new Date(prev);
                d.setDate(d.getDate() - 7);
                return d;
            });
        } else {
            setCurrentMonth(prev => {
                if (prev.month === 0) {
                    return { year: prev.year - 1, month: 11 };
                }
                return { year: prev.year, month: prev.month - 1 };
            });
        }
    }, [viewMode]);

    const handleNext = useCallback(() => {
        if (viewMode === "day") {
            setCurrentDayDate(prev => {
                const d = new Date(prev);
                d.setDate(d.getDate() + 1);
                return d;
            });
        } else if (viewMode === "week") {
            setCurrentWeekDate(prev => {
                const d = new Date(prev);
                d.setDate(d.getDate() + 7);
                return d;
            });
        } else {
            setCurrentMonth(prev => {
                if (prev.month === 11) {
                    return { year: prev.year + 1, month: 0 };
                }
                return { year: prev.year, month: prev.month + 1 };
            });
        }
    }, [viewMode]);

    const handleToday = useCallback(() => {
        const now = new Date();
        setCurrentMonth({ year: now.getFullYear(), month: now.getMonth() });
        setCurrentWeekDate(now);
        setCurrentDayDate(now);
    }, []);

    const handleSetViewMode = useCallback(
        (mode: ViewMode) => {
            if (mode === "week" && viewMode === "month") {
                const now = new Date();
                const isCurrentMonth =
                    currentMonth.year === now.getFullYear() &&
                    currentMonth.month === now.getMonth();
                setCurrentWeekDate(
                    isCurrentMonth
                        ? now
                        : new Date(currentMonth.year, currentMonth.month, 1),
                );
            } else if (mode === "month" && viewMode === "week") {
                setCurrentMonth({
                    year: currentWeekDate.getFullYear(),
                    month: currentWeekDate.getMonth(),
                });
            } else if (mode === "day" && viewMode === "month") {
                const now = new Date();
                const isCurrentMonth =
                    currentMonth.year === now.getFullYear() &&
                    currentMonth.month === now.getMonth();
                setCurrentDayDate(
                    isCurrentMonth
                        ? now
                        : new Date(currentMonth.year, currentMonth.month, 1),
                );
            } else if (mode === "month" && viewMode === "day") {
                setCurrentMonth({
                    year: currentDayDate.getFullYear(),
                    month: currentDayDate.getMonth(),
                });
            } else if (mode === "week" && viewMode === "day") {
                setCurrentWeekDate(currentDayDate);
            } else if (mode === "day" && viewMode === "week") {
                setCurrentDayDate(currentWeekDate);
            }
            dispatch(calendarSlice.actions.setViewMode(mode));
        },
        [dispatch, viewMode, currentMonth, currentWeekDate, currentDayDate],
    );

    const handleMiniCalPrev = useCallback(() => {
        setMiniCalMonth(prev => {
            if (prev.month === 0) return { year: prev.year - 1, month: 11 };
            return { year: prev.year, month: prev.month - 1 };
        });
    }, []);

    const handleMiniCalNext = useCallback(() => {
        setMiniCalMonth(prev => {
            if (prev.month === 11) return { year: prev.year + 1, month: 0 };
            return { year: prev.year, month: prev.month + 1 };
        });
    }, []);

    const handleMiniCalSelect = useCallback(
        (date: Date) => {
            if (viewMode === "day") {
                setCurrentDayDate(date);
            } else if (viewMode === "week") {
                setCurrentWeekDate(date);
            } else {
                setCurrentMonth({
                    year: date.getFullYear(),
                    month: date.getMonth(),
                });
            }
            setMiniCalExpanded(false);
        },
        [viewMode],
    );

    const toggleMiniCal = useCallback(() => {
        setMiniCalExpanded(prev => !prev);
    }, []);

    const handleDayClick = useCallback(
        (date: Date) => {
            setCurrentDayDate(date);
            dispatch(calendarSlice.actions.setViewMode("day"));
        },
        [dispatch],
    );

    const handleEventClick = useCallback((event: CalendarEvent) => {
        if (event.kind === "tour") {
            setSelectedTour(event.data);
            setSelectedTask(null);
        } else {
            setSelectedTask(event.data);
            setSelectedTour(null);
        }
    }, []);

    const handleTourSaved = useCallback(() => {
        toursQuery.refetch();
    }, [toursQuery]);

    const handleTourClose = useCallback(() => {
        setSelectedTour(null);
    }, []);

    const handleTaskClose = useCallback(() => {
        setSelectedTask(null);
    }, []);

    const todayKey = formatDateKey(today);

    const headerMonthLabel = useMemo(() => {
        if (viewMode === "day") {
            return MONTH_NAMES[currentDayDate.getMonth()];
        }
        if (viewMode === "week") {
            return MONTH_NAMES[currentWeekDate.getMonth()];
        }
        return MONTH_NAMES[currentMonth.month];
    }, [viewMode, currentDayDate, currentWeekDate, currentMonth]);

    return (
        <Stage>
            <PageHeader title="Calendar" />
            <Content>
                <div>
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-2">
                            <button
                                onClick={handlePrev}
                                className="p-1.5 rounded hover:bg-gray-100">
                                <ChevronLeftIcon className="h-5 w-5 text-gray-600" />
                            </button>
                            <button
                                onClick={handleNext}
                                className="p-1.5 rounded hover:bg-gray-100">
                                <ChevronRightIcon className="h-5 w-5 text-gray-600" />
                            </button>
                            <button
                                onClick={toggleMiniCal}
                                className="flex items-center gap-1 ml-2">
                                <span className="text-lg font-semibold text-gray-900">
                                    {headerMonthLabel}
                                </span>
                                <ChevronDownIcon
                                    className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${
                                        miniCalExpanded ? "rotate-180" : ""
                                    }`}
                                />
                            </button>
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={handleToday}
                                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                                Today
                            </button>
                            <div className="w-48" data-testid="calendar-user-filter">
                                <UserSelect
                                    label=""
                                    value={selectedUserId ?? currentUserId}
                                    onChange={handleUserChange}
                                    includeEveryone={false}
                                    annotateMe={true}
                                />
                            </div>
                            <Menu as="div" className="relative">
                                <MenuButton className="flex items-center gap-x-1.5 rounded-md bg-white px-3 py-1.5 text-sm font-medium text-gray-700 border border-gray-300 hover:bg-gray-50">
                                    {viewMode === "month"
                                        ? "Month"
                                        : viewMode === "day"
                                          ? "Day"
                                          : "Week"}
                                    <ChevronDownIcon className="-mr-1 h-5 w-5 text-gray-400" />
                                </MenuButton>
                                <MenuItems
                                    transition
                                    className="absolute right-0 z-10 mt-3 w-24 origin-top-right overflow-hidden rounded-md bg-white shadow-lg outline outline-1 outline-black/5">
                                    <div className="py-1">
                                        <MenuItem>
                                            <button
                                                onClick={() => handleSetViewMode("day")}
                                                className="block w-full px-4 py-2 text-left text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900">
                                                Day
                                            </button>
                                        </MenuItem>
                                        <MenuItem>
                                            <button
                                                onClick={() => handleSetViewMode("week")}
                                                className="block w-full px-4 py-2 text-left text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900">
                                                Week
                                            </button>
                                        </MenuItem>
                                        <MenuItem>
                                            <button
                                                onClick={() => handleSetViewMode("month")}
                                                className="block w-full px-4 py-2 text-left text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900">
                                                Month
                                            </button>
                                        </MenuItem>
                                    </div>
                                </MenuItems>
                            </Menu>
                        </div>
                    </div>

                    {miniCalExpanded && viewMode === "month" && (
                        <MonthPicker
                            currentMonth={currentMonth}
                            onSelect={(year, month) => {
                                setCurrentMonth({ year, month });
                                setMiniCalExpanded(false);
                            }}
                        />
                    )}

                    {miniCalExpanded && viewMode !== "month" && (
                        <div className="border border-gray-200 rounded-lg p-3 mb-4 bg-white">
                            <div className="flex items-center justify-between mb-2">
                                <button
                                    onClick={handleMiniCalPrev}
                                    className="p-1 rounded hover:bg-gray-100">
                                    <ChevronLeftIcon className="h-4 w-4 text-gray-600" />
                                </button>
                                <span className="text-sm font-medium text-gray-900">
                                    {MONTH_NAMES[miniCalMonth.month]} {miniCalMonth.year}
                                </span>
                                <button
                                    onClick={handleMiniCalNext}
                                    className="p-1 rounded hover:bg-gray-100">
                                    <ChevronRightIcon className="h-4 w-4 text-gray-600" />
                                </button>
                            </div>
                            <div className="grid grid-cols-7 mb-1">
                                {DAYS_OF_WEEK.map(d => (
                                    <div
                                        key={d}
                                        className="text-center text-xs font-medium text-gray-500 py-1">
                                        {d.charAt(0)}
                                    </div>
                                ))}
                            </div>
                            <div className="grid grid-cols-7">
                                {miniCalDates.map(({ date, isCurrentMonth }) => {
                                    const cellKey = formatDateKey(date);
                                    const isCellToday = cellKey === todayKey;
                                    const isSelected =
                                        viewMode === "day"
                                            ? isSameDay(date, currentDayDate)
                                            : viewMode === "week"
                                              ? isSameDay(date, currentWeekDate)
                                              : false;
                                    return (
                                        <button
                                            key={cellKey}
                                            onClick={() => handleMiniCalSelect(date)}
                                            className={`flex items-center justify-center h-8 w-full text-xs rounded-full ${
                                                isCellToday
                                                    ? "bg-brand-600 text-white font-semibold"
                                                    : isSelected
                                                      ? "bg-brand-200 font-semibold text-brand-800"
                                                      : isCurrentMonth
                                                        ? "text-gray-900 hover:bg-gray-100"
                                                        : "text-gray-400 hover:bg-gray-50"
                                            }`}>
                                            {date.getDate()}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {viewMode === "month" && (
                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                            <div className="grid grid-cols-7">
                                {DAYS_OF_WEEK.map(day => (
                                    <div
                                        key={day}
                                        className="px-2 py-2 text-xs font-medium text-gray-500 text-center bg-gray-50 border-b border-gray-200">
                                        {day}
                                    </div>
                                ))}
                            </div>

                            <div className="grid grid-cols-7">
                                {gridDates.map(({ date, isCurrentMonth }, idx) => {
                                    const dateKey = formatDateKey(date);
                                    const isToday = dateKey === todayKey;
                                    const dayEvents = eventsByDate.get(dateKey) || [];
                                    const showBorder =
                                        idx % 7 !== 0 ? "border-l border-gray-200" : "";
                                    const rowBorder =
                                        idx >= 7 ? "border-t border-gray-200" : "";

                                    return (
                                        <div
                                            key={dateKey}
                                            onClick={() => handleDayClick(date)}
                                            className={`min-h-24 p-1 ${showBorder} ${rowBorder} ${
                                                !isCurrentMonth ? "bg-gray-50" : ""
                                            } cursor-pointer`}>
                                            <div className="flex justify-end">
                                                <span
                                                    className={`text-sm inline-flex items-center justify-center w-7 h-7 rounded-full ${
                                                        isToday
                                                            ? "bg-brand-600 text-white font-semibold"
                                                            : isCurrentMonth
                                                              ? "text-gray-900"
                                                              : "text-gray-400"
                                                    }`}>
                                                    {date.getDate()}
                                                </span>
                                            </div>
                                            <div className="space-y-0.5 mt-0.5">
                                                {dayEvents.map(evt => {
                                                    if (evt.kind === "tour") {
                                                        return (
                                                            <button
                                                                key={evt.data.id}
                                                                onClick={e => {
                                                                    e.stopPropagation();
                                                                    handleEventClick(evt);
                                                                }}
                                                                data-testid="calendar-tour-event"
                                                                className="block w-full text-left px-1.5 py-0.5 text-xs rounded bg-brand-50 text-brand-700 hover:bg-brand-100 truncate">
                                                                {evt.data.communityName}
                                                                {evt.data.tourTime
                                                                    ? ` · ${formatTime(evt.data.tourTime)}`
                                                                    : ""}
                                                            </button>
                                                        );
                                                    }
                                                    return (
                                                        <button
                                                            key={evt.data.id}
                                                            onClick={e => {
                                                                e.stopPropagation();
                                                                handleEventClick(evt);
                                                            }}
                                                            data-testid="calendar-task-event"
                                                            className="block w-full text-left px-1.5 py-0.5 text-xs rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100 truncate">
                                                            {evt.data.text}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {viewMode === "week" && (
                        <WeekGrid
                            weekDays={weekDays}
                            today={today}
                            eventsByDate={eventsByDate}
                            onTourClick={handleEventClick}
                            onDayClick={handleDayClick}
                        />
                    )}

                    {viewMode === "day" && (
                        <DayGrid
                            day={currentDayDate}
                            today={today}
                            eventsByDate={eventsByDate}
                            onEventClick={handleEventClick}
                        />
                    )}
                </div>
                {selectedTour ? (
                    <TourEditor
                        tour={selectedTour}
                        communityId={selectedTour.communityId}
                        clientId={selectedTour.clientId}
                        communityName={selectedTour.communityName}
                        clientName={selectedTour.clientName ?? undefined}
                        onSaved={handleTourSaved}
                        onClose={handleTourClose}
                    />
                ) : null}
                {selectedTask ? (
                    <TaskDetailPanel task={selectedTask} onClose={handleTaskClose} />
                ) : null}
            </Content>
        </Stage>
    );
};
